import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { directory, isForbidden } from '@cwos/api-client';
import type { messaging } from '@cwos/api-client';
import { ConsequenceNote, SkeletonSet } from '@cwos/ui-admin';
import { useAuth } from '../../lib/auth.js';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { errorMessage, useCreateChannel } from './messaging.js';
import { LoadFailure } from '../LoadFailure.js';

type Worker = directory.Worker;
type Mode = 'channel' | 'dm';

/**
 * Create a conversation. Two shapes:
 *  - a named channel/announcement with a member picker from the directory, or
 *  - a direct message ("message a person") → `kind:'dm'` with `[me, them]`.
 *
 * The directory is the only source of people (`directory.workers()`); we never
 * invent recipients, and workers without a linked user account can't be added.
 */
export function CreateChannel({
  onCreated,
  onCancel,
}: {
  onCreated: (channelId: string) => void;
  onCancel: () => void;
}) {
  const { me } = useAuth();
  const [mode, setMode] = useState<Mode>('channel');
  const [kind, setKind] = useState<messaging.ChannelKind>('channel');
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const create = useCreateChannel();

  // The people picker is the staff roster, so it answers to the roster's keys.
  // This query carried no `enabled:` at all and fired for any authenticated
  // caller who opened the composer; without `user:read` the server now refuses
  // it.
  // At ORGANIZATION scope, which is where the route decides it: both of its
  // gates are `can()` with no scope argument, so a `user:read` confined to one
  // program is a 403 rather than a shorter roster.
  const permissions = usePermissions();
  const mayList = mayListWorkers(permissions);
  // Default-deny is right for `enabled:` and wrong for the picker's sentence:
  // it is also false while the set is loading and after it fails. Only a
  // resolved set may be turned into a statement about this account.
  const rosterWithheld = !permissions.isLoading && !permissions.isError && !mayList;
  const roster = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: mayList,
  });

  const myUserId = me?.userId ?? '';
  const people = useMemo<Worker[]>(
    () => (roster.data ?? []).filter((w) => w.userId && w.userId !== myUserId),
    [roster.data, myUserId],
  );

  const toggle = (userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const canSubmit =
    mode === 'channel'
      ? name.trim() !== '' && !create.isPending
      : selected.size === 1 && !create.isPending;

  const submit = () => {
    if (!canSubmit) return;
    const input: messaging.CreateChannelInput =
      mode === 'channel'
        ? { kind, name: name.trim(), memberUserIds: Array.from(selected) }
        : {
            kind: 'dm',
            // The server inserts the actor as `owner` automatically; a DM takes
            // exactly ONE other member, so send only the selected person.
            memberUserIds: Array.from(selected),
          };
    create.mutate(input, { onSuccess: (channel) => onCreated(channel.id) });
  };

  const forbidden = create.isError && isForbidden(create.error);

  return (
    <section className="rn-card msg-compose">
      <div className="rn-card-head">
        <h2 className="rn-card-title">New conversation</h2>
        <button type="button" className="rn-link" onClick={onCancel}>
          Cancel
        </button>
      </div>

      <div className="msg-mode-tabs">
        <button
          type="button"
          className="rn-link"
          aria-pressed={mode === 'channel'}
          onClick={() => setMode('channel')}
        >
          A channel
        </button>
        <button
          type="button"
          className="rn-link"
          aria-pressed={mode === 'dm'}
          onClick={() => setMode('dm')}
        >
          Message a person
        </button>
      </div>

      {mode === 'channel' ? (
        <div className="msg-field">
          <label className="msg-label" htmlFor="msg-new-name">
            Name
          </label>
          <input
            id="msg-new-name"
            className="msg-input"
            value={name}
            placeholder="e.g. Morning shift"
            onChange={(e) => setName(e.target.value)}
          />
          <div className="msg-kind-choice" role="radiogroup" aria-label="Conversation type">
            <label className="msg-radio">
              <input
                type="radio"
                name="msg-kind"
                checked={kind === 'channel'}
                onChange={() => setKind('channel')}
              />
              <span>Channel — everyone can post</span>
            </label>
            <label className="msg-radio">
              <input
                type="radio"
                name="msg-kind"
                checked={kind === 'announcement'}
                onChange={() => setKind('announcement')}
              />
              <span>Announcement — for broadcasts</span>
            </label>
          </div>
        </div>
      ) : (
        <p className="rn-muted" style={{ margin: '0 0 var(--space-8)' }}>
          Pick one person to start a private thread.
        </p>
      )}

      <div className="msg-field">
        <span className="msg-label">{mode === 'channel' ? 'Members' : 'Person'}</span>
        <PeoplePicker
          query={roster}
          people={people}
          mayList={mayList}
          withheld={rosterWithheld}
          selected={selected}
          multiple={mode === 'channel'}
          onToggle={toggle}
          onPickOne={(id) => setSelected(new Set([id]))}
        />
      </div>

      <ConsequenceNote
        text={
          mode === 'channel'
            ? 'Everyone you add can see this conversation and its history.'
            : 'This opens a private thread between the two of you.'
        }
      />

      {forbidden ? (
        <p className="msg-error">You don’t have permission to start conversations.</p>
      ) : create.isError ? (
        <p className="msg-error">
          {errorMessage(create.error, 'We couldn’t create that just now.')} Try again.
        </p>
      ) : null}

      <button type="button" className="msg-primary" disabled={!canSubmit} onClick={submit}>
        {create.isPending ? 'Creating…' : mode === 'channel' ? 'Create channel' : 'Start message'}
      </button>
    </section>
  );
}

function PeoplePicker({
  query,
  people,
  selected,
  multiple,
  onToggle,
  mayList,
  withheld,
  onPickOne,
}: {
  query: ReturnType<typeof useQuery<Worker[]>>;
  people: Worker[];
  /**
   * The roster query fired. Default-deny, so it is also false while the
   * permission set is unresolved — which is why it does not decide the
   * sentence below.
   */
  mayList: boolean;
  /**
   * A RESOLVED permission set says the reader does not hold the roster's two
   * keys over the organization. The only state that may be told to them as a
   * fact about their account.
   */
  withheld: boolean;
  selected: Set<string>;
  multiple: boolean;
  onToggle: (userId: string) => void;
  onPickOne: (userId: string) => void;
}) {
  if (withheld) {
    // The query never ran: `GET /directory/workers` needs `user:read` and the
    // server refuses rather than narrowing, so this is disabled by
    // `mayListWorkers`. A disabled query has no data, no loading and no error —
    // it fell straight through `?? []` into "No teammates with accounts to add
    // yet", which is a claim about the whole organization made to the one
    // reader who may not be told anything about it.
    return (
      <p className="rn-muted" role="status">
        Naming the people in your organization needs the directory permission, so we cannot list who
        you could add. This is not an organization with nobody in it.
      </p>
    );
  }
  // Not withheld and not fetched: the permission set has not resolved, so the
  // query is still disabled. That is pending, not denied, and not an empty
  // organization either.
  if (!mayList || query.isLoading) return <SkeletonSet surface="table" rows={4} />;
  if (query.isError && !query.data) {
    return (
      <LoadFailure
        error={query.error}
        message="We couldn’t load the directory just now."
        onRetry={() => void query.refetch()}
      />
    );
  }
  if (people.length === 0) {
    return <p className="rn-muted">No teammates with accounts to add yet.</p>;
  }
  return (
    <ul className="msg-people">
      {people.map((w) => {
        const userId = w.userId as string;
        const on = selected.has(userId);
        return (
          <li key={w.id}>
            <label className="msg-person" data-on={on}>
              <input
                type={multiple ? 'checkbox' : 'radio'}
                name="msg-person"
                checked={on}
                onChange={() => (multiple ? onToggle(userId) : onPickOne(userId))}
              />
              <span className="msg-person-name">{w.displayName}</span>
              {w.roleTitle ? <span className="rn-muted">{w.roleTitle}</span> : null}
            </label>
          </li>
        );
      })}
    </ul>
  );
}
